import { useState, useEffect } from "react";
import { Info, X } from "lucide-react";

/**
 * Global Disclaimer Component
 * IMPORTANT: All elements have pointerEvents: 'none' except dismiss button
 * to prevent blocking touch events on Android WebView
 */
export function GlobalDisclaimer() {
  const [dismissed, setDismissed] = useState(false);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('compass_disclaimer_dismissed');
    if (wasDismissed === 'true') {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('compass_disclaimer_dismissed', 'true');
  };

  // Full disclaimer modal
  const FullDisclaimerModal = () => {
    if (!showFull) return null;
    
    return (
      <div 
        style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', pointerEvents: 'auto' }}
      >
        <div 
          style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', pointerEvents: 'auto' }}
          onClick={() => setShowFull(false)} 
        />
        <div style={{ position: 'relative', backgroundColor: '#0b2821', border: '1px solid rgba(166,147,115,0.3)', borderRadius: '1rem', padding: '1.5rem', maxWidth: '28rem', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', pointerEvents: 'auto' }}>
          <button 
            onClick={() => setShowFull(false)}
            style={{ position: 'absolute', top: '1rem', right: '1rem', padding: '0.25rem', color: 'rgba(166,147,115,0.6)', pointerEvents: 'auto' }}
          >
            <X style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
          
          <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#a69373' }}>
            Important Disclaimer
          </h3>
          
          <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)' }}>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: '#a69373' }}>Compass is a personal development and self-reflection app.</strong> It is NOT a substitute for professional medical, psychological, or mental health services.
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: '#a69373' }}>If you are experiencing mental health challenges, crisis, or distress, please contact a qualified healthcare professional or emergency services immediately.</strong>
            </p>
          </div>
          
          <button
            onClick={() => setShowFull(false)}
            style={{ marginTop: '1.5rem', width: '100%', padding: '0.625rem', borderRadius: '0.75rem', fontSize: '0.875rem', fontWeight: 500, backgroundColor: '#a69373', color: '#0b2821', pointerEvents: 'auto' }}
          >
            I Understand
          </button>
        </div>
      </div>
    );
  };

  // Compact version after dismissal
  if (dismissed) {
    return (
      <>
        <div 
          style={{ position: 'fixed', bottom: '5rem', left: '50%', transform: 'translateX(-50%)', zIndex: 30, pointerEvents: 'none' }}
          data-testid="global-disclaimer-compact"
        >
          <button
            onClick={() => setShowFull(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', backgroundColor: 'rgba(11,40,33,0.8)', border: '1px solid rgba(166,147,115,0.2)', color: 'rgba(166,147,115,0.7)', pointerEvents: 'auto' }}
          >
            <Info style={{ width: '0.75rem', height: '0.75rem', pointerEvents: 'none' }} />
            <span style={{ pointerEvents: 'none' }}>Compass is not medical advice</span>
          </button>
        </div>
        <FullDisclaimerModal />
      </>
    );
  }

  // Expanded version - shown initially
  return (
    <>
      <div 
        style={{ position: 'fixed', bottom: '5rem', left: '50%', transform: 'translateX(-50%)', zIndex: 30, width: 'fit-content', maxWidth: '28rem', padding: '0 1rem', pointerEvents: 'none' }}
        data-testid="global-disclaimer-expanded"
      >
        <div 
          style={{ backgroundColor: 'rgba(11,40,33,0.95)', border: '1px solid rgba(166,147,115,0.3)', borderRadius: '0.75rem', padding: '1rem', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', pointerEvents: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', pointerEvents: 'none' }}>
            <Info style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0, marginTop: '0.125rem', color: '#a69373', pointerEvents: 'none' }} />
            <div style={{ flex: 1, pointerEvents: 'none' }}>
              <p style={{ fontSize: '0.75rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.85)', pointerEvents: 'none' }}>
                <strong style={{ color: '#a69373', pointerEvents: 'none' }}>Compass is not medical advice.</strong>
                <span style={{ pointerEvents: 'none' }}> This app is for self-reflection and personal growth. It is not a substitute for professional mental health support.</span>
              </p>
            </div>
            <button
              onClick={handleDismiss}
              style={{ padding: '0.25rem', flexShrink: 0, color: 'rgba(166,147,115,0.6)', pointerEvents: 'auto' }}
              aria-label="Dismiss"
            >
              <X style={{ width: '1rem', height: '1rem', pointerEvents: 'none' }} />
            </button>
          </div>
        </div>
      </div>
      <FullDisclaimerModal />
    </>
  );
}

export default GlobalDisclaimer;
